import FileUpload from "@mui/icons-material/FileUpload";
import { Alert, Box, Button, Chip, CircularProgress, styled } from "@mui/material";
import { type FC, useCallback, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useToken } from "../../server/auth/use-token";
import { serviceURL } from "../../utils/service-url";
import type { CreateContentRequest } from "../multiformat/store/types";

export const CreateContentFileUpload: FC = () => {
  const { t } = useTranslation();
  const getToken = useToken();
  const [loading, setLoading] = useState<boolean>(false);

  const { control, setValue } = useFormContext<CreateContentRequest>();
  const fileValue = useWatch({ control, name: "file" });

  const handleSubmit = useCallback(
    async (files?: FileList | null) => {
      if (!files || files.length === 0) {
        console.error("No file found");
        return;
      }

      if (files.length > 1) {
        console.error("Can only upload one file at a time");
        return;
      }
      const file = files[0]!;

      if (file.size > 550 * 1024 * 1024) {
        toast.custom(
          <Alert severity="error" sx={{ alignItems: "center", "& .MuiAlert-action": { pt: 0 } }}>
            {t("This file is too large - please try again with a file under 550mb")}
          </Alert>,
        );

        console.error("File size exceeds 550mb");
        return;
      }

      setLoading(true);

      const body = new FormData();
      body.append("purpose", "user_data");
      body.append("file", file);

      const response = await fetch(`${serviceURL("autopilot")}file`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
        body: body,
      });

      if (!response.ok || !response.body) {
        console.error("Failed to upload file");
        toast.error("Failed to upload file");
        return;
      }

      const r = (await response.json()) as { id: string };

      setValue("file", { id: r.id, name: file.name });
      setLoading(false);
    },
    [getToken, setValue, t],
  );

  return (
    <Box
      sx={{
        position: "absolute",
        bottom: 0,
        left: 8,
        padding: 1,
      }}
    >
      {loading ? (
        <CircularProgress size={18} />
      ) : !fileValue ? (
        <Button
          variant="text"
          startIcon={<FileUpload />}
          data-analytics-id="create-content-upload-file-button"
          sx={{
            p: 0,
            textTransform: "none",
            color: "text.secondary",
            fontWeight: "400",
            "&:hover": {
              backgroundColor: "transparent",
              textDecoration: "underline",
            },
          }}
        >
          {t("Upload a pdf, docx, or txt file (max 550mb) to summarize it")}
          <HiddenInput
            type="file"
            accept=".doc,.docx,.pdf,.txt"
            onChange={(event) => {
              void handleSubmit(event.target.files).catch(console.error);
            }}
          />
        </Button>
      ) : (
        <Chip
          size="small"
          color="primary"
          label={fileValue?.name}
          onDelete={() => setValue("file", undefined)}
        />
      )}
    </Box>
  );
};

const HiddenInput = styled("input")({
  position: "absolute",
  width: "100%",
  height: "100%",
  opacity: 0,
  cursor: "pointer",
});
